// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';
import {
    Autocomplete,
    TextField,
    Checkbox,
    Typography,
    CircularProgress,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

interface MerchantOption {
    value: number | string;
    label: string;
}

interface MerchantSelectorProps {
    field: {
        value: any;
        onChange: (value: any) => void;
    };
    merchantList: MerchantOption[];
    multiple?: boolean;
    onSearch?: (query: string) => void;
    onLoadMore?: () => void;
    isLoading?: boolean;
    hasMore?: boolean;
}

export const MerchantSelector: React.FC<MerchantSelectorProps> = ({
    field,
    merchantList,
    multiple = true,
    onSearch,
    onLoadMore,
    isLoading = false,
    hasMore = false,
}) => {
    const [inputValue, setInputValue] = useState('');

    const handleChange = useCallback(
        (_, newValue) => {
            if (multiple) {
                const ids = Array.isArray(newValue)
                    ? newValue.map((item: MerchantOption) => item.value)
                    : [];
                field.onChange(ids);
            } else {
                field.onChange(newValue?.value ?? null);
                setInputValue('');
            }
        },
        [field, multiple]
    );

    const handleInputChange = useCallback((_, newInputValue, reason) => {
        if (multiple && reason === 'reset') return;
        setInputValue(newInputValue);
        if (onSearch && reason === 'input') {
            onSearch(newInputValue);
        }
    }, [multiple, onSearch]);

    const selectedValue = useMemo(() => {
        if (multiple) {
            if (!field.value || !Array.isArray(field.value)) return [];
            return merchantList.filter(o => field.value.some(id => String(id) === String(o.value)));
        }
        if (field.value == null) return null;
        return merchantList.find(o => String(o.value) === String(field.value)) || null;
    }, [field.value, merchantList, multiple]);

    const handleListboxScroll = useCallback((event: React.UIEvent<HTMLUListElement>) => {
        if (!onLoadMore || !hasMore || isLoading) return;
        const el = event.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
            onLoadMore();
        }
    }, [onLoadMore, hasMore, isLoading]);

    const renderOption = useCallback((props, option, { selected }) => (
        <li {...props} key={option.value} style={{ alignItems: 'flex-start', paddingTop: '8px', paddingBottom: '8px' }}>
            {multiple && (
                <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8, marginTop: -9 }}
                    checked={selected}
                />
            )}
            <Typography style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {option.label}
            </Typography>
        </li>
    ), [multiple]);

    const allFilteredSelected = false;
    const handleSelectAll = () => { };

    return (
        <Autocomplete
            multiple={multiple}
            fullWidth
            disableCloseOnSelect={multiple}
            id="merchant-selector"
            options={merchantList}
            value={selectedValue}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleChange}
            loading={isLoading}
            filterOptions={onSearch ? (x) => x : undefined}
            noOptionsText={isLoading ? "Yükleniyor..." : "Sonuç bulunamadı"}
            getOptionLabel={(option) => option?.label ?? ''}
            isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
            renderOption={renderOption}
            ListboxProps={{
                style: { maxHeight: '400px' },
                onScroll: handleListboxScroll,
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Üye İşyeri"
                    placeholder={
                        (multiple && selectedValue?.length === 0) || (!multiple && !selectedValue)
                            ? 'Seçiniz veya arayınız...'
                            : ''
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading && <CircularProgress color="inherit" size={16} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                        ...(multiple && {
                            startAdornment: (
                                <>
                                    <Checkbox
                                        sx={{ padding: '4px' }}
                                        checked={allFilteredSelected}
                                        onChange={handleSelectAll}
                                        title={'Seç'}
                                    />
                                    {params.InputProps.startAdornment}
                                </>
                            )
                        })
                    }}
                />
            )}
        />
    );
};
